import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  Bell,
  Send,
  Users, 
  MessageSquare,
  BellRing,
  CheckCircle,
  XCircle,
  Clock,
  Target,
  Plus,
  Trash2
} from 'lucide-react';
import { useCRMAuth } from '../../contexts/CRMAuthContext';
import { useAuth } from '../../contexts/AuthContext';
import { canManageNotifications } from '../../lib/role';
import { useToast } from '../../hooks/use-toast';

const PushNotificationCenter = ({ canManage } = {}) => {
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCompose, setShowCompose] = useState(false);
  
  // Form state
  const [notificationForm, setNotificationForm] = useState({
    title: '',
    body: '',
    targetAudience: 'all_customers',
    priority: 'normal',
    actionUrl: '',
    imageUrl: '',
    customData: [{ key: '', value: '' }]  // structured key/value rows (no raw JSON)
  });
  
  const { user, makeAuthenticatedRequest } = useCRMAuth();
  const { user: authUser } = useAuth();
  const { toast } = useToast();

  // Defensive RBAC guard (Slice A policy — management only). Prefer the explicit prop from the
  // parent; otherwise derive from the canonical business role. When not allowed we NEVER fetch
  // history or send — this prevents the backend 403 (and its red error banner) for operator/etc.
  const isAllowed = typeof canManage === 'boolean'
    ? canManage
    : canManageNotifications(authUser?.role || user?.role);

  // Load notification history on component mount (only when allowed).
  useEffect(() => {
    if (user && isAllowed) {
      loadNotificationHistory();
    }
  }, [user, isAllowed]);

  const loadNotificationHistory = async () => {
    try {
      setIsLoading(true);
      setError('');

      const response = await makeAuthenticatedRequest('/api/v1/notifications/history', {
        method: 'GET'
      });

      if (!response.ok) {
        const data = await response.json();
        const msg = typeof data.detail === 'string'
          ? data.detail
          : Array.isArray(data.detail)
            ? data.detail.map(e => e.msg).join(', ')
            : 'Failed to load notification history';
        throw new Error(msg);
      }

      const historyData = await response.json();
      setNotifications(historyData.notifications || []);
      
    } catch (error) {
      console.error('Error loading notification history:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendNotification = async () => {
    if (!isAllowed) {
      return;  // defensive — non-management roles cannot send (backend 403s anyway)
    }
    if (!notificationForm.title.trim() || !notificationForm.body.trim()) {
      setError('Please enter both title and message');
      return;
    }

    try {
      setIsLoading(true);
      setError('');

      // Build custom data object from structured key/value rows (no raw JSON authoring).
      // Preserves the exact backend payload contract via controlled serialization.
      const customData = {};
      const seenKeys = new Set();
      for (const row of notificationForm.customData) {
        const key = (row.key || '').trim();
        const value = (row.value || '').trim();
        if (!key && !value) continue;  // skip fully-empty rows
        if (!key) {
          throw new Error('Each custom data field needs a key');
        }
        if (seenKeys.has(key)) {
          throw new Error(`Duplicate custom data key: "${key}"`);
        }
        seenKeys.add(key);
        customData[key] = value;
      }

      const notificationData = {
        title: notificationForm.title,
        body: notificationForm.body,
        target_audience: notificationForm.targetAudience,
        priority: notificationForm.priority,
        action_url: notificationForm.actionUrl || undefined,
        image_url: notificationForm.imageUrl || undefined,
        data: Object.keys(customData).length > 0 ? customData : undefined
      };

      const response = await makeAuthenticatedRequest('/api/v1/notifications/send', {
        method: 'POST',
        body: JSON.stringify(notificationData)
      });

      if (!response.ok) {
        const data = await response.json();
        const msg = typeof data.detail === 'string'
          ? data.detail
          : Array.isArray(data.detail)
            ? data.detail.map(e => e.msg).join(', ')
            : 'Failed to send notification';
        throw new Error(msg);
      }

      const result = await response.json();
      
      toast({
        title: "Notification Sent",
        description: `Successfully sent to ${result.sent_count || 0} recipients`,
      });

      // Reset form and reload history
      setNotificationForm({
        title: '',
        body: '',
        targetAudience: 'all_customers',
        priority: 'normal',
        actionUrl: '',
        imageUrl: '',
        customData: [{ key: '', value: '' }]
      });
      
      setShowCompose(false);
      await loadNotificationHistory();
      
    } catch (error) {
      console.error('Error sending notification:', error);
      setError(error.message);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // --- Structured Custom Data row editor helpers ---
  const addCustomDataRow = () => {
    setNotificationForm(prev => ({
      ...prev,
      customData: [...prev.customData, { key: '', value: '' }]
    }));
  };

  const removeCustomDataRow = (index) => {
    setNotificationForm(prev => {
      const next = prev.customData.filter((_, i) => i !== index);
      // always keep at least one editable row
      return { ...prev, customData: next.length > 0 ? next : [{ key: '', value: '' }] };
    });
  };

  const updateCustomDataRow = (index, field, value) => {
    setNotificationForm(prev => ({
      ...prev,
      customData: prev.customData.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    }));
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-600" />;
      default:
        return <Bell className="w-4 h-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent': return 'bg-green-100 text-green-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getAudienceIcon = (audience) => {
    switch (audience) {
      case 'all_customers':
      case 'active_customers':
        return <Users className="w-3 h-3 mr-1" />;
      case 'staff_members':
      case 'admin_managers':
        return <Users className="w-3 h-3 mr-1" />;
      default:
        return <Users className="w-3 h-3 mr-1" />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!user) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <p className="text-gray-500">Please log in to access Push Notification Center</p>
        </CardContent>
      </Card>
    );
  }

  // Honest restricted state for non-management roles — no history fetch, no compose, no refresh,
  // no red 403 banner. Mirrors the backend Slice A policy without loosening it.
  if (!isAllowed) {
    return (
      <Card>
        <CardContent className="p-6 text-center space-y-2">
          <Bell className="h-6 w-6 text-gray-400 mx-auto" />
          <p className="text-gray-600 font-medium">Push Notifications are restricted</p>
          <p className="text-gray-500 text-sm">
            Sending and viewing notification history is available to management roles
            (admin, manager, director, executive). Contact your administrator if you need access.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex-1 h-full overflow-y-auto p-6">
      <div className="space-y-4 max-w-6xl mx-auto">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center">
              <BellRing className="w-5 h-5 mr-2 text-purple-600" />
              Push Notification Center
            </div>
            <div className="flex items-center space-x-2">
              <Badge variant="outline" className="bg-blue-50 text-blue-700">
                Admin/Manager Only
              </Badge>
              <Button onClick={() => setShowCompose(true)} disabled={isLoading} data-testid="compose-notification-btn">
                <Send className="w-4 h-4 mr-2" />
                Compose
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="border-green-200 bg-green-50">
              <CardContent className="p-4 text-center">
                <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-green-700">
                  {notifications.filter(n => n.status === 'sent').length}
                </p>
                <p className="text-sm text-green-600">Sent</p>
              </CardContent>
            </Card>
            
            <Card className="border-yellow-200 bg-yellow-50">
              <CardContent className="p-4 text-center">
                <Clock className="w-6 h-6 text-yellow-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-yellow-700">
                  {notifications.filter(n => n.status === 'pending').length}
                </p>
                <p className="text-sm text-yellow-600">Pending</p>
              </CardContent>
            </Card>
            
            <Card className="border-red-200 bg-red-50">
              <CardContent className="p-4 text-center">
                <XCircle className="w-6 h-6 text-red-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-red-700">
                  {notifications.filter(n => n.status === 'failed').length}
                </p>
                <p className="text-sm text-red-600">Failed</p>
              </CardContent>
            </Card>
            
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="p-4 text-center">
                <Target className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-blue-700">
                  {notifications.reduce((acc, n) => acc + (n.sent_count || 0), 0)}
                </p>
                <p className="text-sm text-blue-600">Total Recipients</p>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

      {/* Error Display */}
      {error && (
        <Alert className="border-red-200 bg-red-50">
          <AlertDescription className="text-red-800">
            {error}
          </AlertDescription>
        </Alert>
      )}

      {/* Compose Notification Modal */}
      {showCompose && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Compose Push Notification
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="title">Notification Title *</Label>
                <Input
                  id="title"
                  placeholder="Important Update"
                  value={notificationForm.title}
                  onChange={(e) => setNotificationForm(prev => ({ ...prev, title: e.target.value }))}
                  maxLength={50}
                  data-testid="notification-title-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {notificationForm.title.length}/50 characters
                </p>
              </div>
              
              <div>
                <Label htmlFor="targetAudience">Target Audience</Label>
                <Select 
                  value={notificationForm.targetAudience}
                  onValueChange={(value) => setNotificationForm(prev => ({ ...prev, targetAudience: value }))}
                >
                  <SelectTrigger data-testid="audience-select">
                    <SelectValue placeholder="Select audience" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_customers">All Customers</SelectItem>
                    <SelectItem value="active_customers">Active Customers</SelectItem>
                    <SelectItem value="staff_members">Staff Members</SelectItem>
                    <SelectItem value="admin_managers">Admin & Managers</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="body">Message *</Label>
              <Textarea
                id="body"
                placeholder="Your notification message here..."
                value={notificationForm.body}
                onChange={(e) => setNotificationForm(prev => ({ ...prev, body: e.target.value }))}
                rows={3}
                maxLength={200}
                data-testid="notification-body-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                {notificationForm.body.length}/200 characters
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="priority">Priority</Label>
                <Select 
                  value={notificationForm.priority}
                  onValueChange={(value) => setNotificationForm(prev => ({ ...prev, priority: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="actionUrl">Action URL (Optional)</Label>
                <Input
                  id="actionUrl"
                  placeholder="https://example.com/action"
                  value={notificationForm.actionUrl}
                  onChange={(e) => setNotificationForm(prev => ({ ...prev, actionUrl: e.target.value }))}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="imageUrl">Image URL (Optional)</Label>
              <Input
                id="imageUrl"
                placeholder="https://example.com/image.jpg"
                value={notificationForm.imageUrl}
                onChange={(e) => setNotificationForm(prev => ({ ...prev, imageUrl: e.target.value }))}
              />
            </div>

            <div>
              <Label>Custom Data (Optional)</Label>
              <p className="text-xs text-gray-500 mt-1 mb-2">
                Attach optional key/value pairs to this notification. Leave blank if not needed.
              </p>
              <div className="space-y-2">
                {notificationForm.customData.map((row, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      placeholder="Key"
                      value={row.key}
                      onChange={(e) => updateCustomDataRow(index, 'key', e.target.value)}
                      data-testid={`custom-data-key-${index}`}
                    />
                    <Input
                      placeholder="Value"
                      value={row.value}
                      onChange={(e) => updateCustomDataRow(index, 'value', e.target.value)}
                      data-testid={`custom-data-value-${index}`}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => removeCustomDataRow(index)}
                      aria-label="Remove custom data field"
                      data-testid={`custom-data-remove-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={addCustomDataRow}
                data-testid="add-custom-data-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Field
              </Button>
            </div>
            
            <div className="flex space-x-2">
              <Button onClick={handleSendNotification} disabled={isLoading} data-testid="send-notification-btn">
                {isLoading ? 'Sending...' : 'Send Notification'}
              </Button>
              <Button variant="outline" onClick={() => setShowCompose(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Notification History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Notification History</span>
            <Button variant="outline" size="sm" onClick={loadNotificationHistory} disabled={isLoading} data-testid="refresh-notifications-btn">
              {isLoading ? 'Loading...' : 'Refresh'}
            </Button>
          </CardTitle>
        </CardHeader>
        
        <CardContent>
          {notifications.length === 0 ? (
            <p className="text-center text-gray-500 py-8">
              No notifications sent yet. Compose your first notification to get started.
            </p>
          ) : (
            <div className="space-y-3">
              {notifications.map((notification, index) => (
                <div
                  key={notification.id || index}
                  className="flex items-start justify-between p-4 border rounded-lg hover:bg-gray-50"
                  data-testid={`notification-row-${notification.id || index}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      {getStatusIcon(notification.status)}
                      <h4 className="font-medium">{notification.title}</h4>
                      <Badge variant="outline" className={getStatusColor(notification.status)}>
                        {notification.status}
                      </Badge>
                      {notification.priority !== 'normal' && (
                        <Badge variant="outline" className="capitalize">
                          {notification.priority}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{notification.body}</p>
                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                      <span>Sent: {formatDate(notification.created_at)}</span>
                      <span>Recipients: {notification.sent_count || 0}</span>
                      <span className="flex items-center">
                        {getAudienceIcon(notification.target_audience)}
                        {notification.target_audience?.replace('_', ' ') || 'Unknown'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      </div>
    </div>
  );
};

export default PushNotificationCenter;